// The Areas sheet: every geometry in the project, split by baked section and
// then by level — one row per floor, not one row per drawn shape. Read-only:
// it exists to be summed and filtered (and now, via `mergeAreaRows`, to be
// what `GI.*` formulas sum and filter by default — see nativeMeasures.ts and
// engine/fns/gi.ts), not to be typed into. The Features sheet, alongside it,
// is where a feature's own raw properties are actually edited.
//
// Read-only is enforced by `writeAreas` always overwriting every cell
// (`preserveEdits` hardcoded `false` — see its own doc comment), NOT by
// disguising every field as `calculated`. A cell's TEXT COLOUR instead says
// what it actually is: the sheet's ordinary colour for a field genuinely raw
// on the feature, `var(--gt-muted)` grey for one inherited from the
// feature's matching Usage row, `var(--gt-accent)` blue for pure
// engine/geometry output or a field gTables itself synthesises
// (`stackSection`, `levelHeight`, `levelBaseHeight`, `levelNumber`,
// `buildingID`) — see `entityTable.ts`'s `colourCell`. Colour is cosmetic;
// what actually stops an edit from ever reaching the project is the
// overwrite-on-every-write guarantee.

import type { CellValue, Sheet, Workbook } from '@/model/types';
import { naturalCompare } from '@/model/collate';
import { createSheet, getCell, newId, setCellValue, sheetByRole, uniqueSheetName } from '@/model/workbook';
import { applyStructural, deletionMap } from '@/model/structure';
import type { DataRow, GiraffeData } from '@/data/types';
import { unitsOf } from '@/data/units';
import { NON_MEASURE_FIELDS } from '@/data/classify';
import { rowColToA1, translateFormula } from '@/model/address';
import { compileExpression, toExcelFormula, ExpressionError } from '@/data/expression';
import { featureLabel, SHOW_FIELD } from './featureLabel';
import { usageOptions, usageRowsByName } from './usagesTable';
import {
  FIRST_DATA_ROW,
  HEADER_ROW,
  bold,
  colourCell,
  columnsOf,
  fieldColumns,
  findField,
  orderByLayerTree,
  resetRowOrder,
  rowsByName,
  sortRows,
  squashField,
  writeTable,
  type CellProvenance,
  type EntityRow,
  type TableSpec,
} from './entityTable';

export { SHOW_FIELD };

export const AREAS_SHEET_NAME = 'Areas';

/**
 * The pinned front block, always in this order regardless of provenance.
 * Column letters below count the sheet's own column A as the name/label
 * column (every row's Usage/Block/Building composition) — NOT part of this
 * array — so this array's own index 0 lands in column B, not A. No Show
 * column (user-requested removal — Areas has no switch of its own to flip
 * any more; "Select on map" in the toolbar is still there):
 *   A name · B Usage · C Layer Id · D layerGroup (renamed from
 *   `_layerGroup` — CONFIRMED live by the user, who added one to the real
 *   project specifically so this could be checked; user-specified at
 *   column D) · E `buildingID` (Giraffe's own raw spelling, not gTables'
 *   invented `_buildingId` — see `BUILDING_ID_SQUASHED`) · F-I the four
 *   level fields (`levelNumber`, `levelBaseHeight`, `levelHeight`,
 *   `stackSection`, in that order — user-specified).
 * Everything else sorts into the groups `areaColumnSort` computes
 * (calculated areas/lengths, raw properties, baked properties —
 * custom-calculation columns are formulas appended separately, not fields
 * at all).
 */
const COLUMN_ORDER = [
  'usage',
  'layerId',
  'layerGroup',
  'buildingID',
  'levelNumber',
  'levelBaseHeight',
  'levelHeight',
  'stackSection',
];

/**
 * Fields with no column at all: the handles, not the facts.
 *
 * `id`, `appId` and `projectId` are how the host addresses a feature, and every
 * one of them is the same opaque string on every row of a project — three
 * columns of noise between the name and the numbers, none of them anything to
 * read or to type over. The id is still what the Show switch is addressed by;
 * it is looked up from the row label (`areaIds`), never from a cell.
 *
 * `public` is the project's sharing flag, not a fact about the geometry, and it
 * reads the same on every feature — a column of identical TRUEs sitting where
 * the numbers want to be.
 *
 * `blockId` (user-requested) is another handle, not a fact: it names the
 * Block DEFINITION a feature instances, which `featureLabel`/`blockName`
 * already folds into the row's own display name — a raw id column next to
 * that is a second, less readable copy of the same information.
 *
 * `blockPropagation` (user-requested) is engine bookkeeping about how a
 * block's edits spread to its other instances — not a fact about this
 * geometry's area, height or usage, and nothing on this sheet reads it.
 */
const HIDDEN = new Set(['id', 'appid', 'projectid', 'public', 'blockid', 'blockpropagation']);

/**
 * A baked-section field NAME that Giraffe's own Analytics engine sometimes
 * uses for a value that is really just another field's, under a different
 * spelling (`UsageType` for the section's own `usage` name). Suppressed only
 * when the VALUE actually matches, not by name alone — `UsageType` is also a
 * perfectly ordinary name for a user's own custom Usage Editor field, and an
 * unrelated field sharing the name is real data, not Giraffe's duplicate.
 */
const BAKED_DUPLICATE_FIELDS = new Set(['usagetype']);

/** Is this baked field Giraffe's own duplicate-of-`usage`, by VALUE, not just by name? */
function isBakedUsageDuplicate(squashed: string, value: unknown, usageName: string): boolean {
  if (!BAKED_DUPLICATE_FIELDS.has(squashed)) return false;
  return typeof value === 'string' && value.trim().toLowerCase() === usageName;
}

/**
 * Named out regardless of classification — confirmed live: `flow` and
 * `marker` sometimes arrive pre-stringified (a JSON STRING, not an object),
 * which the nested-value detection below does not catch (it only classifies
 * an object as `nested`; a string that happens to BE serialised JSON reads
 * as ordinary text) — so a name-based net under it, not instead of it.
 * `fromLayer` was never nested to begin with, just noise ("Giraffe drawing
 * (default)" on every feature drawn by hand).
 */
const EXCLUDED_FIELDS = new Set(['flow', 'marker', 'fromlayer']);

/**
 * The building-id field, squashed — matched against a raw/baked field
 * regardless of its exact spelling. See `findField`'s doc comment: Giraffe's
 * own spelling is `buildingID` (raw) / presumably `_buildingID` (baked),
 * never the camelCase `_buildingId` a literal access might assume — this
 * sheet's own dedicated column is named `buildingID` to match the project's
 * real spelling (see `COLUMN_ORDER`), not gTables' own invented name for it.
 */
const BUILDING_ID_SQUASHED = squashField('buildingID');

/**
 * The few underscore-prefixed fields this sheet exposes on purpose, keyed by
 * their public (no-underscore) column name — `_layerGroup` was already one
 * of these; `_parkingBayCount` is user-requested, same treatment.
 */
const UNDERSCORE_ALIASES: Record<string, string> = {
  _layerGroup: 'layerGroup',
  _parkingBayCount: 'parkingBayCount',
};

/**
 * Every underscore-prefixed field is the engine's own internal bookkeeping
 * (`_evaluatedId`, `_blockLayerPath`, `_baseElevation`/`_baseHeightDatum` —
 * confirmed live as baked-only near-duplicates of `baseHeight`, same pattern
 * as `_blockId`/`_stackOrder` mirroring their bare raw names — `_fillOpacity`,
 * `_layerOpacity`, `_strokeOpacity`, `_visible`, and more) — user-requested,
 * removed wholesale rather than named one at a time, except the ones this
 * sheet handles on purpose: `UNDERSCORE_ALIASES` above (renamed, no
 * underscore, see `emitRow`), the building id (its own dedicated
 * `buildingID` field, below — whatever its baked spelling, matched via
 * `BUILDING_ID_SQUASHED` and also excluded from this generic sweep via
 * `classify.ts`'s `NON_MEASURE_FIELDS`), and `_baseHeight`/`_height`
 * (consumed by `levelBaseHeight`/`levelHeight`/`levelNumber` below, not
 * exposed as raw-named columns of their own).
 */
function isInternalUnderscoreField(field: string): boolean {
  return field.startsWith('_') && !(field in UNDERSCORE_ALIASES);
}

/**
 * Every drawing layer a feature could be put on.
 *
 * The union of two answers, because neither alone is the list. The project's
 * layer tree knows about a layer nothing has been drawn on yet — which is
 * exactly the layer someone wants to move a feature TO — and the features
 * themselves are proof of a layer whatever the tree's undocumented shape turns
 * out to be on a given build. A value off the list still goes in; it is drawn
 * as an off-list chip rather than refused.
 */
function layerOptions(data: GiraffeData): string[] {
  const out = new Set<string>();
  for (const row of data.rows('rawSections')) {
    const layer = row.properties.layerId;
    if (typeof layer === 'string' && layer.trim() !== '') out.add(layer.trim());
  }
  const treeOrder = data.drawingLayers?.() ?? [];
  for (const layer of treeOrder) out.add(layer);
  // Tree order, not alphabetical (user-requested) — same order the rows
  // themselves sort by (`sortRows`), so the dropdown and the sheet agree.
  return orderByLayerTree(out, treeOrder);
}

/** Where `mergeAreaRows` found each discovered field, across every row. */
export interface MergedAreas {
  rows: EntityRow[];
  /** A field at least one feature has as a genuine raw property. */
  rawFields: Set<string>;
  /** A field no feature has raw, and whose baked value is NOT also carried by
   *  the feature's matching Usage row — pure engine output (an area, a dwelling
   *  count, …), as opposed to a cost rate or style merely copied down. */
  calculatedFields: Set<string>;
  /** A field no feature has raw, but whose baked value IS also carried by the
   *  feature's matching Usage row — a cost rate, a colour, anything merely
   *  copied down rather than computed. Disjoint from `calculatedFields`: a
   *  field is either pure engine output or a Usage copy, never both. */
  inheritedFields: Set<string>;
  /** Row label (lowercased) -> the underlying RAW feature id, for the Show
   *  switch — see `areaIds`. */
  rowIds: Map<string, string>;
}

const EMPTY_HIDDEN: ReadonlySet<string> = new Set();

/**
 * The rows: one per `bakedSections` row, not one per raw feature — a block
 * made of several usage-split parts, or several placements of one flow, is
 * exactly that many SEPARATE geometries, and showing it as a single
 * aggregated row ("2+ values" everywhere two parts disagree) hides the thing
 * the sheet exists to show. Each gets its own raw-feature properties joined
 * on by `id` (confirmed live, including for an instanced block placement —
 * see README.md), its own matching Usage row for `inherited` fields, and its
 * own baked values directly — no aggregation needed any more, because each
 * output row already corresponds to exactly one baked row.
 *
 * A raw feature with NO baked row yet (freshly drawn, not baked) still gets
 * ONE row built from its raw properties alone, so it is not invisible until
 * the next bake — matching the sheet's existing backward-compat guarantee
 * (a project that never triggers a baked field simply never shows one).
 *
 * A field's provenance is NOT forced to `calculated` — see the module
 * comment's own explanation of why colour and read-only-ness are two
 * different guarantees now. Editing a feature's raw properties lives on the
 * Features sheet instead.
 *
 * Row naming: `featureLabel`'s Usage/Block/Building composition, which is
 * lossy by design — several rows can land on the exact same text (a block
 * decomposed into several usage-split parts all share one Usage+Block+
 * Building; two unnamed "Road" segments share nothing at all to tell them
 * apart). NO disambiguating suffix any more (user-requested removal) — a
 * label two rows share is simply shown on both. What still tells them
 * apart is `EntityRow.key` (the underlying feature/section's own raw id),
 * which `sortRows` never reads but "Select on map" does — see its own doc
 * comment.
 *
 * `hidden` is accepted for signature compatibility with `areaRows`/
 * `areaColumns`/`writeAreas`, but no longer read in this function: its only
 * use was the Show field's value, and Show is gone (user-requested
 * removal) — there is no column left for it to drive.
 *
 * A baked row with `levels > 1` splits AGAIN, into one row per level —
 * the engine answers with one stack total, not one row per floor, so a
 * 9-level tower would otherwise report nine floors' worth of dwellings as if
 * it were one. Every field classified `total` (see classify.ts — grossArea,
 * dwellingTotalCount, …: `levels` already applied) is divided by the level
 * count; `rate` fields (salePrice, efficiency, …) are already per-level and
 * carry over unchanged. `levels` is left alone — it stays the SECTION's true
 * total level count on every row, the same number on all of them, since it
 * describes the stack the row is one floor of, not the row itself.
 *
 * Four fields describe where in the building a row's level sits — set on
 * EVERY row, not only a split one: a single-level section still has exactly
 * one level, with its own height and base, same as any other.
 *   - `stackSection` (1-based) — which floor of THIS SECTION's own stack a
 *     row is, named so it does not collide with `levels`.
 *   - `levelHeight` — the section's own `_height` (baked), DIVIDED by the
 *     level count first: `_height` is a `total`-class value (the SECTION's
 *     whole height, the same `levels * floorHeight` shape as `grossArea`/
 *     `netArea`, not one floor's) — NOT the plain `height` property, a
 *     different, independent number left as its own raw column. EXCEPT for
 *     a `type: basement` section (user-specified): `_height` is not
 *     trusted there, so `depth` (a RAW feature property, the basement's own
 *     total excavation depth) divided by the level count is used instead
 *     where a project sets it, falling back to `floorToFloor` — already a
 *     per-floor rate (`classify.ts`'s `RATE_FIELDS`), so used as-is, read
 *     off the feature directly first, then its matching Usage row.
 *   - `levelBaseHeight` — `_baseHeight` (baked), incremented by the
 *     DIVIDED `levelHeight` for every level past the first: `_baseHeight +
 *     (stackSection - 1) * levelHeight`. Falls back to the bare
 *     `_baseHeight` (no increment) when `_height` is missing, rather than
 *     omitting the field entirely. A `type: basement` section ignores
 *     `_baseHeight` too (user-specified): Ground (elevation 0) is the one
 *     reference every basement counts down from, but belongs to Ground
 *     itself, never to a basement level — B1 (`levelNumber` -1) is already
 *     one `levelHeight` BELOW Ground, at `-levelHeight`, B2 at
 *     `-2*levelHeight`, and so on — falling back to a flat 0 (Ground
 *     itself) on every level when `levelHeight` is unknown, since there is
 *     then nothing to subtract by.
 *   - `levelNumber` — which floor of the whole BUILDING this is, Ground = 0:
 *     every LEVEL of every section sharing a `buildingID` contributes its
 *     own `levelBaseHeight` (the per-floor value, not the section's bare
 *     `_baseHeight`) to one pool for the whole building; the DISTINCT
 *     values are sorted ascending and ranked from 0 — a 2-level podium
 *     under a 12-level tower of the same building ranks all 14 real
 *     floors, not the two sections against each other, and two levels
 *     (from different sections) that land on the exact same elevation
 *     share one rank, same as they share one real floor. A section whose
 *     `type` is `basement` ranks in its own pool instead, Ground-relative
 *     and negative: the deepest basement level is the most negative
 *     number, and the one directly below Ground is always -1 — never 0 or
 *     positive, and never mixed into the regular, above-ground ranking.
 *     Omitted where there is no `buildingID` to rank within.
 *
 * `plan` fields (area, perimeter: one level's footprint) always carry over
 * UNCHANGED, on every split level — level 2 of a tower genuinely has the
 * same footprint as level 1, and showing a third of it would be wrong.
 *
 * User-requested removal: this used to also divide a plan field when read
 * as `'areas'` for `GI.*` aggregation (`areaDataRows`'s own `forAggregation`
 * option, now gone) — summing one across a level-split stack naively
 * overcounts a 9-level tower's footprint ninefold, since the same number
 * sits on all nine rows, and dividing first made `GI.TOTAL("area")` land
 * back on the true figure. But that meant a plain Excel `SUM` over the
 * SHEET's own (undivided) area column disagreed with what `GI.TOTAL`
 * computed from the exact same rows — two "correct" answers that did not
 * match each other. Removed so both read the sheet the same, undivided, way
 * — `GI.TOTAL("area")`/`GI.FOOTPRINT()` now overcount a level-split stack
 * exactly as a hand-written `SUM` would, instead of silently disagreeing
 * with it. `stampSchedule`'s Area schedule no longer offers a footprint-class
 * field as a stampable column at all, so a NEW schedule cannot reproduce the
 * overcount it would invite — see its own doc comment.
 */
function numberOf(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

export function mergeAreaRows(data: GiraffeData, _hidden: ReadonlySet<string> = EMPTY_HIDDEN): MergedAreas {
  const rawById = new Map<string, DataRow>();
  for (const row of data.rows('rawSections')) rawById.set(String(row.id), row);
  const usageByName = usageRowsByName(data);
  const blocks = data.blocks();

  const rawFields = new Set<string>();
  const calculatedFields = new Set<string>();
  const inheritedFields = new Set<string>();

  interface PreRow {
    id: string;
    base: string;
    fields: Record<string, unknown>;
    provenance: Record<string, CellProvenance>;
  }
  const pre: PreRow[] = [];

  interface LevelInfo {
    /** 1-based — which floor of THIS SECTION's own stack. */
    index: number;
    count: number;
    /** The section's own `_height` (baked), same on every one of its levels. */
    height: number | undefined;
    /** `_baseHeight`, incremented by `_height` for every level past the first. */
    baseHeight: number | undefined;
  }

  /** Whether a section is a basement, by its own `type` (raw or baked). */
  function isBasementSection(raw: DataRow | undefined, baked: DataRow | undefined): boolean {
    const type = findField(baked?.properties, 'type') ?? findField(raw?.properties, 'type');
    return typeof type === 'string' && type.trim().toLowerCase() === 'basement';
  }

  /** A section's own level count — `levels`, 2 or more, else a single level. */
  function countOf(raw: DataRow | undefined, baked: DataRow | undefined): number {
    const levels = baked
      ? numberOf(findField(raw?.properties, 'levels') ?? findField(baked.properties, 'levels'))
      : undefined;
    return levels !== undefined && levels >= 2 ? Math.round(levels) : 1;
  }

  /**
   * A section's own per-level height. `floorToFloor` — already a per-floor
   * rate (see `classify.ts`'s `RATE_FIELDS`), so used as-is, never divided —
   * wins whenever a project sets it, read off the feature directly first,
   * then its matching Usage row. Confirmed live: for a project that draws
   * one feature per LEVEL rather than one multi-level stack, baked `_height`
   * is not the section's own floor-to-floor thickness at all but the
   * ABSOLUTE top elevation of that single slab (`_height` − `_baseHeight`
   * equalling the real `floorToFloor` exactly confirmed this) — dividing
   * that by the level count (1) leaked the full elevation through as
   * `levelHeight`, reading as though height had been multiplied by the
   * level count. Only once no `floorToFloor` is set anywhere does `_height`
   * (baked, `total`-class — the SECTION's whole height, the same `levels *
   * floorHeight` shape as `grossArea`/`netArea`) divided by the level count
   * get used, same as every other `total` field in the baked-copy loop
   * below. A `type: basement` section additionally prefers `depth` (a RAW
   * feature property, a basement's own total excavation depth) divided by
   * the level count ABOVE `floorToFloor` — user-specified: `_height` is
   * never trusted there at all, not even as a last resort.
   */
  function levelHeightOf(raw: DataRow | undefined, baked: DataRow | undefined, count: number): number | undefined {
    const floorToFloorOf = (): number | undefined => {
      const direct = numberOf(findField(baked?.properties, 'floorToFloor') ?? findField(raw?.properties, 'floorToFloor'));
      if (direct !== undefined) return direct;
      const usageSource = baked?.properties.usage ?? raw?.properties.usage;
      const usageName = typeof usageSource === 'string' ? usageSource.trim().toLowerCase() : '';
      const usageRow = usageName !== '' ? usageByName.get(usageName) : undefined;
      return numberOf(usageRow?.floorToFloor);
    };
    if (isBasementSection(raw, baked)) {
      const depth = numberOf(findField(raw?.properties, 'depth'));
      if (depth !== undefined) return depth / count;
      return floorToFloorOf();
    }
    const floorToFloor = floorToFloorOf();
    if (floorToFloor !== undefined) return floorToFloor;
    const rawHeight = numberOf(baked?.properties._height);
    return rawHeight !== undefined ? rawHeight / count : undefined;
  }

  /**
   * The building a section ranks its levels within. A real `buildingID` wins
   * outright; failing that, user-specified: assume everything sharing a
   * `layerId` is one building, since that is what a layer usually IS when a
   * project never bothered naming one explicitly. Failing even that (no
   * layerId either), the section's own id, so it still ranks against
   * itself rather than vanishing from every grouping below.
   */
  function buildingGroupKey(raw: DataRow | undefined, baked: DataRow | undefined): string {
    const buildingId = findField(baked?.properties, 'buildingID') ?? findField(raw?.properties, 'buildingID');
    if (typeof buildingId === 'string' && buildingId.trim() !== '') return buildingId;
    const layerId = baked?.properties.layerId ?? raw?.properties.layerId;
    if (typeof layerId === 'string' && layerId.trim() !== '') return `__layer__${layerId}`;
    return `__section__${baked?.id ?? raw?.id}`;
  }

  /**
   * Where a basement section's OWN top level sits, relative to Ground (0) —
   * keyed by the section's baked id. A lone basement section starts at 0,
   * same as before. But user-specified: two or more basement SECTIONS
   * sharing the same `buildingID` AND `layerId` are one continuous
   * basement, stacked by `stackOrder` — the HIGHEST `stackOrder` sits
   * closest to Ground (nearest 0), and each section below it in the stack
   * starts exactly where the one above it ends (its top offset minus that
   * section's own full depth, `count * height`), so the whole stack reads
   * as one uninterrupted run of floors, not several independently-zeroed
   * sections that happen to overlap. With no `buildingID` at all, falls
   * back to the coarser `buildingGroupKey` (layerId alone, or the section
   * itself) — same fallback the level-number ranking below uses.
   */
  const basementTopOffsetOf = new Map<string, number>();
  {
    const groups = new Map<string, { id: string; raw: DataRow | undefined; baked: DataRow }[]>();
    for (const baked of data.rows('bakedSections')) {
      const raw = rawById.get(String(baked.id));
      if (!isBasementSection(raw, baked)) continue;
      const buildingId = findField(baked.properties, 'buildingID') ?? findField(raw?.properties, 'buildingID');
      const layerId = baked.properties.layerId ?? raw?.properties.layerId;
      const key =
        typeof buildingId === 'string' && buildingId.trim() !== ''
          ? `${buildingId} ${typeof layerId === 'string' ? layerId : ''}`
          : buildingGroupKey(raw, baked);
      const list = groups.get(key) ?? [];
      list.push({ id: String(baked.id), raw, baked });
      groups.set(key, list);
    }
    for (const sections of groups.values()) {
      sections.sort((a, b) => {
        const stackOrderOf = (s: typeof a) => numberOf(findField(s.baked.properties, 'stackOrder') ?? findField(s.raw?.properties, 'stackOrder')) ?? 0;
        return stackOrderOf(b) - stackOrderOf(a); // highest stackOrder first — closest to Ground
      });
      let offset = 0;
      for (const { id, raw, baked } of sections) {
        basementTopOffsetOf.set(id, offset);
        const count = countOf(raw, baked);
        const height = levelHeightOf(raw, baked, count) ?? 0;
        offset -= count * height;
      }
    }
  }

  /**
   * Every level a section splits into, in order — the exact same
   * count/height/baseHeight arithmetic `buildRow` below used to run
   * inline, pulled out so the `levelNumber` ranking table (next) and the
   * actual emitted rows always agree on each level's own `baseHeight`:
   * computed once, by one function, not twice by two copies of the same
   * formula that could quietly drift apart.
   *
   * A basement section ignores its own raw `_baseHeight` entirely —
   * user-specified: Ground (elevation 0) is the one reference point every
   * basement counts down from, not whatever a project happened to type into
   * `_baseHeight` (or, stacked with a sibling basement section sharing its
   * `buildingID`/`layerId`, wherever `basementTopOffsetOf` placed it in that
   * stack). The level directly below Ground (B1, `levelNumber` -1) sits a
   * full `height` BELOW that offset, not at it — user-specified: elevation
   * 0 belongs to Ground itself, never to a basement level, so B1 is already
   * one floor down, B2 two, and so on — the same convention a real
   * basement is numbered by. Unknown `height` (no `depth` and no
   * `floorToFloor` anywhere) falls back to a flat value at that offset on
   * every level of that section, rather than guessing a depth.
   */
  function computeLevels(raw: DataRow | undefined, baked: DataRow | undefined): LevelInfo[] {
    const count = countOf(raw, baked);
    const height = levelHeightOf(raw, baked, count);
    const isBasement = isBasementSection(raw, baked);
    const baseHeight = numberOf(baked?.properties._baseHeight);
    const topOffset = isBasement && baked ? (basementTopOffsetOf.get(String(baked.id)) ?? 0) : 0;
    const out: LevelInfo[] = [];
    for (let i = 0; i < count; i++) {
      let levelBaseHeight: number | undefined;
      if (isBasement) {
        // `topOffset + (i - count) * height` — B1 (i === count - 1) lands
        // at `topOffset - height`, a full floor below Ground, not AT
        // `topOffset` (Ground's own elevation belongs to the floor above,
        // never to a basement level).
        levelBaseHeight = height !== undefined ? topOffset + (i - count) * height : topOffset;
      } else {
        levelBaseHeight = baseHeight !== undefined && height !== undefined ? baseHeight + i * height : baseHeight;
      }
      out.push({ index: i + 1, count, height, baseHeight: levelBaseHeight });
    }
    return out;
  }

  /**
   * `levelNumber`'s ranking table, built once over every LEVEL of every
   * baked section sharing a building id — not just once per section — so a
   * building made of a 2-level podium and a 12-level tower above it ranks
   * all 14 actual floors, Ground = 0 through the top, instead of ranking
   * the two SECTIONS against each other. Every level's own `baseHeight`
   * (the same incremented value `levelBaseHeight` shows) is collected
   * across the whole building, using the same `computeLevels` arithmetic
   * `buildRow` uses to emit the rows themselves, so a level's rank always
   * matches a real floor in the data, not a guess at one.
   *
   * A section whose `type` is `basement` ranks in its OWN pool, separate
   * from the rest of the building: sorted ascending (deepest first) and
   * numbered backwards from the ground floor — the deepest basement is the
   * most negative, and the one immediately below Ground is always -1,
   * matching how a real building's basement levels (B1, B2, …) are
   * numbered, never 0 or positive.
   *
   * The regular and basement ranks are kept in TWO separate maps, not one
   * merged by `baseHeight` — confirmed live as the actual bug: a regular
   * section and a basement section with no `baseHeight` of their own both
   * default to the same number (commonly 0), and a single shared map had
   * the basement's negative rank silently overwrite the regular section's
   * at that same key, handing an above-ground row a basement's negative
   * `levelNumber`. Looking a row up in the pool that matches its OWN
   * `isBasementSection` status, below, is what actually keeps them apart —
   * distinct `baseHeight` values alone are not guaranteed.
   */
  const levelsByBuilding = new Map<string, { regular: Set<number>; basement: Set<number> }>();
  for (const baked of data.rows('bakedSections')) {
    const raw = rawById.get(String(baked.id));
    // No building to rank within still ranks — see `buildingGroupKey` above.
    // Without this, a tower nobody got around to naming a building for
    // showed no levelNumber at all, below.
    const groupKey = buildingGroupKey(raw, baked);
    const isBasement = isBasementSection(raw, baked);
    const entry = levelsByBuilding.get(groupKey) ?? { regular: new Set<number>(), basement: new Set<number>() };
    const heights = isBasement ? entry.basement : entry.regular;
    for (const level of computeLevels(raw, baked)) {
      if (level.baseHeight !== undefined) heights.add(level.baseHeight);
    }
    levelsByBuilding.set(groupKey, entry);
  }
  const levelNumberOf = new Map<string, { regular: Map<number, number>; basement: Map<number, number> }>();
  for (const [buildingId, { regular, basement }] of levelsByBuilding) {
    const regularRank = new Map([...regular].sort((a, b) => a - b).map((h, i) => [h, i]));
    const sortedBasement = [...basement].sort((a, b) => a - b);
    const belowGround = sortedBasement.length;
    // deepest (i=0) -> -belowGround; nearest Ground -> -1
    const basementRank = new Map(sortedBasement.map((h, i) => [h, i - belowGround]));
    levelNumberOf.set(buildingId, { regular: regularRank, basement: basementRank });
  }

  function emitRow(id: string, raw: DataRow | undefined, baked: DataRow | undefined, level: LevelInfo): void {
    const fields: Record<string, unknown> = {};
    const provenance: Record<string, CellProvenance> = {};

    if (raw) {
      for (const [f, v] of Object.entries(raw.properties)) {
        const lower = f.toLowerCase();
        if (EXCLUDED_FIELDS.has(lower)) continue;
        // A JSON-shaped value (dwellingMix, …) has no business as a sheet
        // cell — it is never a measure, never editable here, and writing
        // its full structure out balloons the sheet's formula count for a
        // value nobody can read in a cell anyway. Left out entirely, not
        // just summarised: "don't include to start with".
        if (data.classOf('rawSections', f) === 'nested') continue;
        if (isInternalUnderscoreField(f)) continue;
        // Whatever this project spells its building id as, it becomes the
        // dedicated `buildingID` column below, not a second, raw-spelled
        // copy of the same value.
        if (squashField(f) === BUILDING_ID_SQUASHED) continue;
        const outField = UNDERSCORE_ALIASES[f] ?? f;
        fields[outField] = v;
        if (!HIDDEN.has(squashField(outField))) rawFields.add(outField);
      }
    }

    const usageSource = baked?.properties.usage ?? raw?.properties.usage;
    const usageName = typeof usageSource === 'string' ? usageSource.trim().toLowerCase() : '';
    const usageRow = usageName !== '' ? usageByName.get(usageName) : undefined;

    if (baked) {
      for (const field of Object.keys(baked.properties)) {
        const squashed = squashField(field);
        const lower = field.toLowerCase();
        if (
          HIDDEN.has(squashed) ||
          isBakedUsageDuplicate(squashed, baked.properties[field], usageName) ||
          NON_MEASURE_FIELDS.has(lower) ||
          EXCLUDED_FIELDS.has(lower) ||
          squashed === BUILDING_ID_SQUASHED || // same dedicated column, whatever this project spells it as
          data.classOf('bakedSections', field) === 'nested' || // see the matching raw-loop comment above
          isInternalUnderscoreField(field)
        ) {
          continue;
        }
        const outField = UNDERSCORE_ALIASES[field] ?? field;
        // Raw already owns this field on Features, by name.
        if (raw && outField in raw.properties) continue;
        let value = baked.properties[field];
        if (value === null || value === undefined) continue;
        if (typeof value === 'number') {
          const cls = data.classOf('bakedSections', field);
          if (cls === 'total') value = value / level.count;
        }
        fields[outField] = value;
        const inherited = usageRow !== undefined && outField in usageRow;
        provenance[outField] = inherited ? 'inherited' : 'calculated';
        if (inherited) inheritedFields.add(outField);
        else calculatedFields.add(outField);
      }
    }

    // Four fields on EVERY row, split or not — see the module doc comment
    // above `mergeAreaRows` for what each one means and where it comes from.
    fields.stackSection = level.index;
    provenance.stackSection = 'calculated';
    calculatedFields.add('stackSection');
    if (level.height !== undefined) {
      fields.levelHeight = level.height;
      provenance.levelHeight = 'calculated';
      calculatedFields.add('levelHeight');
    }
    if (level.baseHeight !== undefined) {
      fields.levelBaseHeight = level.baseHeight;
      provenance.levelBaseHeight = 'calculated';
      calculatedFields.add('levelBaseHeight');
    }

    // The building id is swept out of the baked-properties loop above by
    // NON_MEASURE_FIELDS (bookkeeping, not a measure) but IS groupable —
    // classify.ts's GROUPABLE_NON_MEASURES already carves it out for exactly
    // this reason — so an Area Schedule "By Building" needs it as a real
    // field on every row, not just folded into the row label below. Read
    // with `findField`, not a literal property access: Giraffe spells it
    // `buildingID` raw (confirmed live), not the camelCase `_buildingId` a
    // literal `.{_buildingId}` access would assume.
    const buildingId = findField(baked?.properties, 'buildingID') ?? findField(raw?.properties, 'buildingID');
    const hasBuildingId = typeof buildingId === 'string' && buildingId.trim() !== '';
    if (hasBuildingId) {
      fields.buildingID = buildingId;
      provenance.buildingID = 'calculated';
      calculatedFields.add('buildingID');
    }

    // Ranked via `buildingGroupKey` (same fallback as the pre-pass above),
    // not nested inside `hasBuildingId`: a building-less row still gets a
    // real levelNumber, it just shows no buildingID alongside it.
    const pool = levelNumberOf.get(buildingGroupKey(raw, baked));
    const ranks = isBasementSection(raw, baked) ? pool?.basement : pool?.regular;
    const rank = level.baseHeight !== undefined ? ranks?.get(level.baseHeight) : undefined;
    if (rank !== undefined) {
      fields.levelNumber = rank;
      provenance.levelNumber = 'calculated';
      calculatedFields.add('levelNumber');
    }

    // A field absent from `provenance` reads as `raw` by `entityTable.ts`'s
    // own documented convention — exactly what a genuinely raw passthrough
    // field (the loop at the top of this function) IS, and what lets it
    // show in the sheet's ordinary (black) text colour instead of every
    // field looking alike. The sheet is still fully read-only regardless of
    // colour — see `writeAreas`'s `preserveEdits` (always `false` now) and
    // the module comment at the top of this file.

    const blockId = raw?.properties.blockId;
    const blockName = typeof blockId === 'string' ? blocks[blockId]?.name : undefined;
    const base = featureLabel(usageSource, buildingId, blockName);

    pre.push({ id, base, fields, provenance });
  }

  function buildRow(id: string, raw: DataRow | undefined, baked: DataRow | undefined): void {
    // No usage, no row — user-requested. The same precedence `emitRow` uses
    // for `usageSource` below, checked once up front rather than per level:
    // a feature with no usage at all has nothing for this sheet to group or
    // sum it under, so it is left out entirely rather than showing as a
    // blank-usage row.
    const usageSource = baked?.properties.usage ?? raw?.properties.usage;
    if (typeof usageSource !== 'string' || usageSource.trim() === '') return;

    for (const level of computeLevels(raw, baked)) emitRow(id, raw, baked, level);
  }

  const bakedRows = data.rows('bakedSections');
  const handled = new Set<string>();
  for (const baked of bakedRows) {
    const id = String(baked.id);
    handled.add(id);
    buildRow(id, rawById.get(id), baked);
  }
  for (const raw of rawById.values()) {
    const id = String(raw.id);
    if (handled.has(id)) continue;
    buildRow(id, raw, undefined);
  }

  // No " (1)"/" (2)" disambiguator — user-requested removal. Two rows CAN
  // now show the identical name; `key` (the underlying feature/section's
  // own raw id) is what "Select on map" actually resolves by — see
  // `EntityRow.key`'s own doc comment. `rowIds` stays name-keyed, collapsing
  // duplicates to whichever came last — a fallback for a sheet that has not
  // been written since this shipped and so has no `entityKeys` yet.
  const rowIds = new Map<string, string>();
  const rows: EntityRow[] = pre.map((p) => {
    rowIds.set(p.base.toLowerCase(), p.id);
    return { name: p.base, key: p.id, fields: p.fields, provenance: p.provenance };
  });

  // By layer (tree order), then building, then level, then usage, then name
  // — see `entityTable.ts`'s `sortRows`. The sheet is a list to scan, not a
  // log of bake order.
  sortRows(rows, data.drawingLayers?.() ?? []);

  return { rows, rawFields, calculatedFields, inheritedFields, rowIds };
}

/** A squashed field name that reads as an area, a length or a perimeter — `area`, `grossArea`, `grossBuildingArea`, `netArea`, `saleableArea`, `length`, `perimeter`, … */
function isAreaOrLengthField(field: string): boolean {
  const s = squashField(field);
  return s.includes('area') || s === 'length' || s === 'perimeter';
}

/** A squashed field name that reads as a dwelling count — `dwellingTotalCount`, `dwelling-1BED-Count`, … */
function isDwellingField(field: string): boolean {
  return squashField(field).startsWith('dwelling');
}

/**
 * The pinned front block first, then four sorted groups:
 *
 * 1. Calculated areas, lengths and the perimeter (engine-computed, no raw or
 *    Usage counterpart, and named like one) — A-Z, except `length` and
 *    `perimeter`, pulled out and reinserted right after `saleableArea`
 *    (user-specified position, not alphabetical — falls back to the end of
 *    the group if a project has no `saleableArea` of its own).
 * 2. Every raw property, every field inherited from the matching Usage row,
 *    AND every other genuinely `calculated` field left over (pure
 *    engine/geometry output with no raw or Usage counterpart, and not an
 *    area/length/perimeter/dwelling field either) — all three merged into
 *    ONE A-Z group (user-requested; these used to sort as two separate
 *    tiers — raw+inherited together, "other calculated" trailing on its
 *    own). The distinction between them still shows, just as text colour,
 *    not position: see the module comment's colour explanation.
 * 3. Dwelling counts A-Z (user-requested, moved out on their own so they sit
 *    together rather than scattered alphabetically through group 2).
 * 4. Custom-calculation columns, which are not fields at all — appended
 *    separately, once this sort has already run, and coloured the same
 *    blue as any other calculated/synthesised field (user-requested — see
 *    `writeCustomCalcColumns`).
 *
 * Assignment is exclusive and raw-first throughout: a field with at least
 * one raw occurrence belongs with group 2 even on a project where it is
 * also, on some other feature, a calculated area or a dwelling count.
 */
function areaColumnSort(
  rawFields: ReadonlySet<string>,
  calculatedFields: ReadonlySet<string>,
  inheritedFields: ReadonlySet<string>
) {
  const az = (a: string, b: string): number => naturalCompare(a, b);
  const calcAreaOrder = (fields: string[]): string[] => {
    const length = fields.find((f) => squashField(f) === 'length');
    const perimeter = fields.find((f) => squashField(f) === 'perimeter');
    const rest = fields.filter((f) => f !== length && f !== perimeter).sort(az);
    const insert = [length, perimeter].filter((f): f is string => f !== undefined);
    if (insert.length === 0) return rest;
    const at = rest.findIndex((f) => squashField(f) === 'saleablearea');
    const index = at < 0 ? rest.length : at + 1;
    return [...rest.slice(0, index), ...insert, ...rest.slice(index)];
  };
  return (fields: string[]): string[] => {
    const remaining = new Set(fields);
    const pinned = COLUMN_ORDER.filter((f) => remaining.delete(f));
    const raw = [...remaining].filter((f) => rawFields.has(f));
    raw.forEach((f) => remaining.delete(f));
    const calcAreas = [...remaining].filter((f) => calculatedFields.has(f) && isAreaOrLengthField(f));
    calcAreas.forEach((f) => remaining.delete(f));
    const dwelling = [...remaining].filter((f) => isDwellingField(f));
    dwelling.forEach((f) => remaining.delete(f));
    const inherited = [...remaining].filter((f) => inheritedFields.has(f));
    inherited.forEach((f) => remaining.delete(f));
    // Raw, Usage-inherited, AND whatever other calculated field has nowhere
    // else to go — all merged into one A-Z group (user-requested; these
    // used to sort as two separate tiers).
    const rawInheritedAndOtherCalculated = [...raw, ...inherited, ...remaining].sort(az);
    return [...pinned, ...calcAreaOrder(calcAreas), ...rawInheritedAndOtherCalculated, ...dwelling.sort(az)];
  };
}

function spec(data: GiraffeData): TableSpec {
  const layers = layerOptions(data);
  const usages = usageOptions(data);
  const { rawFields, calculatedFields, inheritedFields } = mergeAreaRows(data);
  return {
    // Plural, deliberately: a real baked/raw field is itself called `area`
    // (singular), and `entityTable.ts`'s `nameColumn` matches this label
    // case-insensitively against EVERY header on the sheet, not just column
    // 1 — a `label: 'Area'` collides with that field's own header the moment
    // column 1's text ever changes for any reason, silently misidentifying
    // the `area` VALUE column as the name column. Confirmed live: exactly
    // this collision, triggered by an accidental column-1 rename, wrote row
    // labels into the `area` column and left column 1 permanently blank.
    label: 'Areas',
    labelFields: new Set(['name', 'title']),
    order: COLUMN_ORDER,
    hidden: HIDDEN,
    lists: {
      ...(layers.length > 0 ? { layerId: { options: layers } } : {}),
      ...(usages ? { usage: usages } : {}),
    },
    isNested: (field) => data.classOf('rawSections', field) === 'nested' || data.classOf('bakedSections', field) === 'nested',
    columnSort: areaColumnSort(rawFields, calculatedFields, inheritedFields),
    units: unitsOf(data),
    // The project's own property names, verbatim — Show is this app's own
    // switch, not a JSON property, and keeps its pretty label.
    rawHeaders: (field) => field !== SHOW_FIELD,
  };
}

/**
 * The rows, with the Show switch folded in as if it were a field.
 *
 * Folding it in rather than special-casing it everywhere means the column
 * ordering, the header matching and the baseline all handle it for free.
 */
export function areaRows(data: GiraffeData, hidden: ReadonlySet<string>): EntityRow[] {
  return mergeAreaRows(data, hidden).rows;
}

/**
 * Feature id by row label, so a sheet row can be traced back to the map —
 * built from the SAME row-naming `mergeAreaRows` uses, so a disambiguated
 * sibling row ("Residential DxDe (2)") resolves to its shared raw id exactly
 * like the unsuffixed row does.
 */
export function areaIds(data: GiraffeData): Map<string, string> {
  return mergeAreaRows(data).rowIds;
}

export function areaColumns(data: GiraffeData, hidden: ReadonlySet<string>): string[] {
  return columnsOf(areaRows(data, hidden), spec(data));
}

/**
 * `mergeAreaRows`'s output, reshaped into plain `DataRow`s — what makes
 * `'areas'` usable as a `SourceKind` like any other (see model/types.ts):
 * `GI.*` formulas, schedules and live blocks already know how to read
 * `data.rows(source)`, and this is what they get back for `'areas'`. The id
 * is the row's own feature id.
 *
 * `name` is the row's OWN label only as a FALLBACK, for a row with no `name`
 * property of its own — a real `name` field (whatever Giraffe or the user
 * put there) wins. The Areas row label is a derived Usage/Block/Building
 * composition (see featureLabel.ts), not a restatement of that field, and
 * `GI.NTH("name", …)`/`GI.WHERE("name", …)` must still read the PROJECT's
 * own name, not this sheet's own display text for the row.
 */
export function areaDataRows(data: GiraffeData, hidden: ReadonlySet<string> = new Set()): DataRow[] {
  // Same undivided plan fields as the sheet itself (user-requested — see
  // the module doc comment above `mergeAreaRows`): a plain SUM over this
  // source (GI.FOOTPRINT(), GI.TOTAL("area"), a schedule groupBy) now
  // overcounts a level-split stack's footprint exactly as a hand-written
  // Excel SUM over the sheet's own area column would, rather than
  // reconstructing a "true" total that then disagreed with it.
  // The id is the row's own feature (`row.key`), not a lookup by display
  // name: names can repeat now, and a name lookup gave two different
  // sections the same id. The key has to be unique per row, and a feature's
  // level-split rows (or a block's stamped copies) share one id, so it adds
  // the label and, failing that, a counter.
  const { rows } = mergeAreaRows(data, hidden);
  const seen = new Map<string, number>();
  return rows.map((row) => {
    const id = row.key ?? row.name;
    const base = `${id}|${row.name}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return {
      key: n === 0 ? base : `${base}#${n}`,
      id,
      properties: { name: row.name, ...row.fields },
    };
  });
}

export function ensureAreasSheet(wb: Workbook): Sheet {
  let sheet = sheetByRole(wb, 'areas');
  if (!sheet) {
    sheet = createSheet(uniqueSheetName(wb, AREAS_SHEET_NAME), newId('areas'));
    sheet.role = 'areas';
    sheet.colWidths[1] = 190;
    sheet.colWidths[2] = 74;
    wb.sheets.push(sheet);
  }
  /*
   * Show is a two-value column, so it arrives as a dropdown.
   *
   * The chip is what makes the sheet readable at a glance — a column of green
   * TRUEs with three grey FALSEs in it says what is hidden without reading a
   * word — and it is also the only affordance saying the cell has two
   * settings rather than being free text someone has to spell right.
   *
   * Reasserted on EVERY call, not just when the sheet is first created — see
   * the matching comment in `featureTable.ts`'s `ensureFeaturesSheet`, same
   * fix, same reasoning.
   */
  sheet.dropdowns = { ...(sheet.dropdowns ?? {}), 2: { options: ['TRUE', 'FALSE'], colours: ['#6fa86a', '#57606a'] } };
  return sheet;
}

/**
 * Take out a column the sheet used to have and should no longer.
 *
 * A system sheet's CELLS are not saved — they are rebuilt from the project on
 * every load — but its column ORDER is, as the header text. So a workbook saved
 * while `id`, `appId` and `projectId` were still columns brings those headers
 * back, and `writeTable` then has nothing to put under them: the column would
 * sit there empty and permanent, with a name and no contents.
 *
 * Deleted properly rather than blanked, through the same structural machinery a
 * user's own Delete Column goes through, so the widths, filters, dropdowns and
 * any formula pointing past it all move with it. Idempotent: once the header is
 * gone there is nothing left to match.
 */
export function dropHiddenAreaColumns(wb: Workbook, sheet: Sheet): void {
  const doomed: number[] = [];
  for (let c = 1; c <= sheet.cols; c++) {
    const v = getCell(sheet, HEADER_ROW, c)?.v;
    if (v === undefined || v === null) continue;
    if (HIDDEN.has(squashField(String(v)))) doomed.push(c);
  }
  if (doomed.length === 0) return;
  applyStructural(wb, sheet, 'col', deletionMap(doomed));
}

/**
 * The custom-calculation columns — tier d, appended after every entity
 * column (tiers a/b/c, written by `writeTable` just before this runs). One
 * column per distinct `GI.SUMEACH`/`AVGEACH`/`MINEACH`/`MAXEACH`/`EACH`
 * expression found anywhere in the workbook — ported from the old dedicated
 * Measures sheet (`measuresTable.ts`'s `writeMeasures`), same-sheet: no
 * `Areas!` prefix, and no separate label-matching pass, since the row IS
 * the feature already.
 *
 * Blank-and-rebuild the region past the last entity column on every call —
 * like the old Measures sheet, a cell here is never typed into, so there is
 * no state to preserve.
 *
 * Two producers feed this, unified into one shape so the writer does not
 * care which: a gTables `GI.*EACH` expression (`gtablesExpressionColumns`
 * below) and a resolved native Giraffe measure
 * (`giraffe/nativeMeasures.ts`'s `resolveNativeMeasures` — kept out of this
 * file, which stays SDK-free, and resolved by the caller instead).
 */
export interface CustomCalcColumn {
  /** The column header — the expression text, or a native measure's name. */
  header: string;
  /** Canonical Areas fields the formula needs — gates materialisation. */
  fields: readonly string[];
  /** The Excel formula TEXT (no leading '='), given a column-ref resolver. */
  template: (ref: (field: string) => string) => string;
  /**
   * Position in Giraffe's own Analytics panel — lower sorts first. Only a
   * native measure carries one; a gTables `GI.*EACH`-declared column has no
   * analytics position at all (it was never a Giraffe measure), so it sorts
   * alphabetically instead, ahead of every column that does have one.
   */
  order?: number;
}

/**
 * One `CustomCalcColumn` per distinct `GI.SUMEACH`-family expression found
 * anywhere in the workbook (the scan lives in `engine.ts`). An expression
 * that does not currently compile (mid-typing) is silently skipped — what
 * cannot be materialised simply waits, same as the old Measures sheet.
 */
export function gtablesExpressionColumns(expressions: readonly string[]): CustomCalcColumn[] {
  const out: CustomCalcColumn[] = [];
  for (const expr of expressions) {
    let fields: string[];
    try {
      fields = compileExpression(expr).fields;
    } catch (err) {
      if (err instanceof ExpressionError) continue;
      throw err;
    }
    out.push({ header: expr, fields, template: (ref) => toExcelFormula(expr, ref) });
  }
  return out;
}

function writeCustomCalcColumns(
  sheet: Sheet,
  rows: readonly EntityRow[],
  entitySpec: TableSpec,
  columns: readonly CustomCalcColumn[]
): void {
  const entityColumns = columnsOf(rows, entitySpec);
  const at = fieldColumns(sheet, entityColumns);
  const nameCol = 1;
  const lastEntityCol = at.size > 0 ? Math.max(nameCol, ...at.values()) : nameCol;
  const startCol = lastEntityCol + 1;

  for (let c = startCol; c <= sheet.cols; c++) {
    for (let r = HEADER_ROW; r <= sheet.rows; r++) delete sheet.cells[`${r},${c}`];
  }

  const rowOf = rowsByName(sheet, nameCol, rows.length);
  // gTables' own expression columns A-Z first (no analytics position to sort
  // by), then native-measure columns in Analytics' own order.
  const az = (a: string, b: string): number => naturalCompare(a, b);
  const sorted = [
    ...columns.filter((c) => c.order === undefined).sort((a, b) => az(a.header, b.header)),
    ...columns.filter((c) => c.order !== undefined).sort((a, b) => a.order! - b.order!),
  ];

  let col = startCol;
  for (const column of sorted) {
    if (column.fields.some((f) => !at.has(f))) continue; // a field with nowhere to point

    setCellValue(sheet, HEADER_ROW, col, column.header);
    bold(sheet, HEADER_ROW, col);

    // Built once against a representative row, then shifted per feature with
    // the same reference-translator copy/paste and fill already use.
    const templateRow = rows.map((row) => rowOf.get(row.name.toLowerCase())).find((r): r is number => r !== undefined);
    if (templateRow === undefined) {
      col += 1;
      continue;
    }

    const template = column.template((field) => rowColToA1(templateRow, at.get(field)!));

    for (const row of rows) {
      const r = rowOf.get(row.name.toLowerCase());
      if (r === undefined) continue;
      const formula = translateFormula(template, r - templateRow, 0);
      sheet.cells[`${r},${col}`] = { f: formula };
      // Same blue as every other calculated/synthesised field (user-requested) —
      // a custom calc column is gTables' own formula, not a project fact either.
      colourCell(sheet, r, col, 'var(--gt-accent)');
    }
    col += 1;
  }

  sheet.cols = Math.max(sheet.cols, col - 1);
}

/**
 * Always a full overwrite, never `preserveEdits` — this sheet has NOTHING
 * that is ever "yours to keep" (no diff/push function exists for it at all,
 * see the module comment), so there is no `preserveEdits` parameter to get
 * wrong. Confirmed the hard way, live: an accidental edit to a header cell
 * survived rewrite after rewrite with the old `preserveEdits = true`
 * default, because nothing about THAT mechanism cares whether a field is
 * read-only — only whether it is in this sheet's own `at.has('area')`-style
 * machinery wasn't the only thing checking; the loop in `writeTable` that
 * decides what to "keep" runs the same regardless. Every write now replaces
 * every cell with the freshly computed value, full stop.
 */
export function writeAreas(
  sheet: Sheet,
  data: GiraffeData,
  hidden: ReadonlySet<string>,
  customCalcColumns: readonly CustomCalcColumn[],
  wb?: Workbook
): void {
  const rows = areaRows(data, hidden);
  const entitySpec = spec(data);
  // Forces `rows`' own sort order (layer → building → level → usage → name,
  // `entityTable.ts`'s `sortRows`) to take PHYSICAL effect on every write —
  // see `resetRowOrder`'s own doc comment for why `writeTable` alone never
  // moves a row that already has one. Safe here specifically because this
  // sheet keeps nothing across writes to lose (`preserveEdits` is always
  // `false`, directly below).
  resetRowOrder(sheet, rows, entitySpec);
  writeTable(sheet, rows, entitySpec, false, wb);
  writeCustomCalcColumns(sheet, rows, entitySpec, customCalcColumns);
}

/**
 * Which features the sheet says are switched off.
 *
 * Always empty now — user-requested removal of the Show column means Areas
 * no longer has a switch of its own, so `columns.indexOf(SHOW_FIELD)` never
 * finds one and this returns early. Kept rather than deleted: `store.ts`
 * still calls it alongside Features' own (still real) Show column, and a
 * function that only ever returns empty is simpler to leave alone than to
 * unwind from its caller.
 */
export function hiddenFromAreasSheet(sheet: Sheet, data: GiraffeData, hidden: ReadonlySet<string>): Set<string> {
  const columns = areaColumns(data, hidden);
  const showCol = columns.indexOf(SHOW_FIELD);
  const out = new Set<string>();
  if (showCol < 0) return out;
  const ids = areaIds(data);
  const last = FIRST_DATA_ROW + areaRows(data, hidden).length + 40;
  for (let r = FIRST_DATA_ROW; r <= last; r++) {
    const label = getCell(sheet, r, 1)?.v;
    if (label === undefined || label === null) continue;
    const id = ids.get(String(label).trim().toLowerCase());
    if (id === undefined) continue;
    if (isOff(getCell(sheet, r, 2 + showCol)?.v)) out.add(id);
  }
  return out;
}

/** FALSE, "no", 0 and an unticked box all mean off. Anything else is on. */
function isOff(value: CellValue | undefined): boolean {
  if (value === false || value === 0) return true;
  if (typeof value !== 'string') return false;
  const t = value.trim().toLowerCase();
  return t === 'false' || t === 'no' || t === 'off' || t === 'hide' || t === '0';
}
